/*
  3D Burger Showcase — Wurst Burger & Chili Co.
  Scroll-driven 360 spin of a real .glb food model, drag-to-rotate in view.
  - Lazy: Three.js + GLTFLoader + model load only when scrolled near.
  - Poster image stays until the model is ready; also the weak-device fallback.
  - EXPLODE_READY: if a future model has multiple named meshes, this splits
    them along Y on scroll instead of spinning. One solid mesh just spins.
*/
(function () {
  'use strict';

  var THREE_URL = (window.WB_BURGER_THREE_URL) || 'https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js';
  var LOADER_URL = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';

  function loadScript(src) {
    return new Promise(function (res, rej) {
      var existing = document.querySelector('script[data-src="' + src + '"]');
      if (existing) {
        if (existing.getAttribute('data-loaded') === '1') { res(); return; }
        existing.addEventListener('load', function () { res(); });
        existing.addEventListener('error', rej);
        return;
      }
      var s = document.createElement('script');
      s.src = src; s.async = true; s.setAttribute('data-src', src);
      s.onload = function () { s.setAttribute('data-loaded', '1'); res(); };
      s.onerror = rej;
      document.head.appendChild(s);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function weakDevice() {
    // Only skip WebGL on genuinely ancient hardware. Phones commonly report
    // deviceMemory of 2 (or don't report it at all on iOS), so gating on <=2
    // wrongly excluded most phones. Require BOTH very low memory AND very few
    // cores, and treat "unknown" as capable.
    var mem = navigator.deviceMemory;
    var cores = navigator.hardwareConcurrency;
    if (mem === undefined && cores === undefined) { return false; }
    return (mem !== undefined && mem <= 1) && (cores !== undefined && cores <= 2);
  }

  function initStage(root) {
    if (root.getAttribute('data-wb-b3d-ready') === '1') { return; }
    root.setAttribute('data-wb-b3d-ready', '1');

    var canvas = root.querySelector('[data-wb-b3d-canvas]');
    var poster = root.querySelector('[data-wb-b3d-poster]');
    var spinner = root.querySelector('[data-wb-b3d-spinner]');
    var hint = root.querySelector('[data-wb-b3d-hint]');
    var modelUrl = root.getAttribute('data-model');
    var autospin = parseFloat(root.getAttribute('data-autospin') || '3') / 1000;
    var scrollSpins = parseFloat(root.getAttribute('data-scroll-spins') || '2');

    if (!canvas || !modelUrl) { return; }

    // Respect low-power devices and reduced-motion: keep the poster, skip WebGL.
    if (prefersReducedMotion() || weakDevice() || !window.WebGLRenderingContext) {
      if (spinner) { spinner.hidden = true; }
      if (hint) { hint.style.opacity = '0'; }
      return;
    }

    if (spinner) { spinner.hidden = false; }

    function fail(reason) {
      console.error('[wb-burger-3d] ' + reason);
      if (spinner) {
        spinner.hidden = false;
        spinner.style.color = '#ff6a5a';
        spinner.style.maxWidth = '80%';
        spinner.style.textAlign = 'center';
        spinner.style.lineHeight = '1.5';
        spinner.textContent = reason;
      }
    }

    console.log('[wb-burger-3d] loading Three.js from', THREE_URL);
    loadScript(THREE_URL)
      .then(function () {
        if (!window.THREE) { throw new Error('THREE global missing after load'); }
        console.log('[wb-burger-3d] Three.js OK, loading GLTFLoader');
        return loadScript(LOADER_URL);
      })
      .then(function () {
        if (!window.THREE || !window.THREE.GLTFLoader) { throw new Error('GLTFLoader missing after load'); }
        console.log('[wb-burger-3d] GLTFLoader OK, booting scene');
        boot();
      })
      .catch(function (err) {
        fail('3D viewer could not load its library (likely blocked by the store\u2019s content-security policy). ' + (err && err.message ? err.message : ''));
      });

    function boot() {
      if (!window.THREE || !window.THREE.GLTFLoader) { return; }
      var THREE = window.THREE;

      var scene = new THREE.Scene();
      var stage = canvas.parentElement;
      var w = stage.clientWidth, h = stage.clientHeight;

      var camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 100);
      camera.position.set(0, 0.4, 6);

      var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h, false);
      if (renderer.outputEncoding !== undefined) { renderer.outputEncoding = THREE.sRGBEncoding; }

      scene.add(new THREE.AmbientLight(0xffffff, 0.7));
      var key = new THREE.DirectionalLight(0xffffff, 1.0); key.position.set(5, 8, 6); scene.add(key);
      var rim = new THREE.DirectionalLight(0xe01b0c, 0.55); rim.position.set(-6, 2, -4); scene.add(rim);
      var fill = new THREE.DirectionalLight(0xffffff, 0.3); fill.position.set(-4, -3, 5); scene.add(fill);

      var pivot = new THREE.Group(); scene.add(pivot);
      var model = null, parts = [], EXPLODE_READY = false;

      new THREE.GLTFLoader().load(modelUrl, function (gltf) {
        model = gltf.scene;

        var box = new THREE.Box3().setFromObject(model);
        var size = box.getSize(new THREE.Vector3());
        var center = box.getCenter(new THREE.Vector3());
        var maxDim = Math.max(size.x, size.y, size.z) || 1;
        var scale = 3.4 / maxDim;
        model.scale.setScalar(scale);
        model.position.sub(center.multiplyScalar(scale));

        model.traverse(function (o) {
          if (o.isMesh) {
            parts.push(o);
            if (o.material) { o.material.needsUpdate = true; }
          }
        });
        // If the model ships as multiple meshes, the explode path is available.
        EXPLODE_READY = parts.length > 1;
        for (var i = 0; i < parts.length; i++) { parts[i].userData.baseY = parts[i].position.y; }

        pivot.add(model);
        if (poster) { poster.classList.add('is-hidden'); }
        if (spinner) { spinner.hidden = true; }
      }, function (xhr) {
        if (spinner && xhr && xhr.total) {
          spinner.textContent = 'Loading model ' + Math.round((xhr.loaded / xhr.total) * 100) + '%';
        }
      }, function (err) {
        console.error('[wb-burger-3d] model load failed', err);
        fail('Could not load the 3D model file. Check the Model URL ends in .glb and is public.');
      });

      var scrollRot = 0, autoRot = 0, dragRot = 0, dragX = -0.05;
      var vx = 0, dragging = false, px = 0, py = 0, userTouched = false;

      function onScroll() {
        var rect = root.getBoundingClientRect();
        var vh = window.innerHeight;
        var total = root.offsetHeight - vh;
        var passed = Math.min(Math.max(-rect.top, 0), total);
        var p = total > 0 ? passed / total : 0;
        scrollRot = p * Math.PI * 2 * scrollSpins;
        if (hint && p > 0.03) { hint.style.opacity = '0'; }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      function down(e) { dragging = true; userTouched = true; canvas.classList.add('is-grabbing'); var t = e.touches ? e.touches[0] : e; px = t.clientX; py = t.clientY; }
      function move(e) {
        if (!dragging) { return; }
        var t = e.touches ? e.touches[0] : e;
        var dx = t.clientX - px, dy = t.clientY - py;
        dragRot += dx * 0.01; vx = dx * 0.01;
        dragX = Math.max(-0.5, Math.min(0.5, dragX + dy * 0.006));
        px = t.clientX; py = t.clientY;
        if (e.cancelable && e.touches) { e.preventDefault(); }
      }
      function up() { dragging = false; canvas.classList.remove('is-grabbing'); }
      canvas.addEventListener('mousedown', down); window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
      canvas.addEventListener('touchstart', down, { passive: true }); canvas.addEventListener('touchmove', move, { passive: false }); canvas.addEventListener('touchend', up);

      function resize() {
        var ww = stage.clientWidth, hh = stage.clientHeight;
        renderer.setSize(ww, hh, false);
        camera.aspect = ww / hh; camera.updateProjectionMatrix();
      }
      window.addEventListener('resize', resize);

      var visible = true;
      document.addEventListener('visibilitychange', function () { visible = !document.hidden; });

      function tick() {
        requestAnimationFrame(tick);
        if (!visible) { return; }
        if (!dragging) {
          if (userTouched) { dragRot += vx; vx *= 0.94; }
          else { autoRot += autospin; }
        }
        pivot.rotation.y = scrollRot + autoRot + dragRot;
        pivot.rotation.x = dragX;
        renderer.render(scene, camera);
      }
      tick();
    }
  }

  function observe(root) {
    if (!('IntersectionObserver' in window)) { initStage(root); return; }
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) { initStage(root); io.disconnect(); }
      }
    }, { rootMargin: '600px 0px' });
    io.observe(root);
  }

  function boot() {
    var roots = document.querySelectorAll('[data-wb-b3d]');
    for (var i = 0; i < roots.length; i++) { observe(roots[i]); }
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }

  document.addEventListener('shopify:section:load', function (e) {
    var n = e.target.querySelector ? e.target.querySelector('[data-wb-b3d]') : null;
    if (n) { observe(n); }
  });
})();
